import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import { PreferredMethod } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import {
  TemplateOutputs,
  TemplateSummaryOutputs,
  TemplateOutputsRunRateTable,
} from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'

const fetchTemplateOutputs = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-outputs?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredMethod=${preferredMethod}`,
  })

  return data
}

export const useTemplateOutputs = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}): UseQueryResult<TemplateOutputs[]> => {
  return useQuery({
    queryKey: [
      'template-outputs',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateOutputs(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: 0,
  })
}

const fetchTemplateComparison = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-comparison?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredMethod=${preferredMethod}`,
  })
  return data
}

export const useTemplateComparison = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}) => {
  return useQuery({
    queryKey: [
      'template-comparison',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateComparison(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: 0,
  })
}

const fetchTemplateRunRateOutputs = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-outputs-run-rate?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredMethod=${preferredMethod}`,
  })

  return data
}

export const useFetchTemplateRunRateOutputs = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}): UseQueryResult<TemplateOutputsRunRateTable[]> => {
  return useQuery({
    queryKey: [
      'template-outputs-run-rate',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateRunRateOutputs(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    refetchOnWindowFocus: false,
    gcTime: 0, // renamed from cacheTime
  })
}

export type UpdateParams = {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
  updateData: TemplateOutputsRunRateTable[]
}

const updateRunRateTable = async ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
  updateData,
}: UpdateParams) => {
  const { data } = await requestV3({
    url: `/template-outputs-run-rate?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredMethod=${preferredMethod}`,
    method: 'put',
    data: updateData,
  })

  return data
}

export const useUpdateRunRateTable = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateRunRateTable,
    onSuccess: () => {
      // Save rewrites TemplateOutputs and TemplateSummaryOutputs Delta tables;
      // invalidate every query that reads from either so the UI refetches.
      queryClient.invalidateQueries({
        queryKey: ['template-outputs-run-rate'],
      })
      queryClient.invalidateQueries({
        queryKey: ['template-summary-outputs'],
      })
      queryClient.invalidateQueries({
        queryKey: ['template-outputs'],
      })
      queryClient.invalidateQueries({
        queryKey: ['template-comparison'],
      })
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

const fetchTemplateSummaryOutputs = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-summary-outputs?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredMethod=${preferredMethod}`,
  })

  return data
}

export const useFetchTemplateSummaryOutputs = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}): UseQueryResult<TemplateSummaryOutputs[]> => {
  return useQuery({
    queryKey: [
      'template-summary-outputs',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateSummaryOutputs(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    enabled: !!projectId && !!scenarioId && !!workcentreId && !!preferredMethod,
    refetchOnWindowFocus: false,
    gcTime: 0,
  })
}
